import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, orderBy, query, where } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocRow, useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { formatDate, opportunityStatusLabel } from '@/lib/format';
import type { Assessment, Opportunity } from '@/types/domain';
import { strings } from '@/strings';

const createOpportunity = callable<{ title: string; gapId: string | null }, { opportunityId: string }>('createOpportunity');

export function OpportunitiesPage() {
  const { claims } = useAuth();
  const orgId = String(claims.orgId || '');
  const { rows, loading, error } = useDocs<Opportunity>(
    orgId ? query(collection(db, 'opportunities'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')) : null,
    [orgId]
  );
  const { row: assessment } = useDocRow<Assessment>(
    orgId ? doc(db, `orgs/${orgId}/assessment/current`) : null, [orgId]
  );

  const [title, setTitle] = useState('');
  const [gapId, setGapId] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await createOpportunity({ title: title.trim(), gapId: gapId || null });
      setTitle('');
      setGapId('');
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.oppsTitle}</h1>

      <form onSubmit={onCreate} className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
        <h2 className="text-sm font-semibold text-ink">{strings.oppNew}</h2>
        <label className="flex flex-col gap-1 text-sm" htmlFor="new-title">
          {strings.oppNewTitle}
          <input id="new-title" value={title} onChange={(e) => setTitle(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor="new-gap">
          {strings.oppFromGap}
          <select id="new-gap" value={gapId} onChange={(e) => setGapId(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2">
            <option value="" />
            {(assessment?.gaps || []).map((gap) => (
              <option key={gap.domainId} value={gap.domainId}>{gap.domainId}</option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={!title.trim() || busy}
          className="self-start min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
          {strings.oppCreate}
        </button>
        {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
      </form>

      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && rows.length === 0 ? <p className="text-muted">{strings.oppsEmpty}</p> : null}

      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={row.id}>
            <Link to={`/org/opportunities/${row.id}`}
              className="flex flex-wrap items-baseline gap-3 rounded-lg border border-border-subtle bg-white p-4 hover:border-primary">
              <span className="font-medium text-ink">{row.title}</span>
              <span className="text-sm text-muted">{opportunityStatusLabel(row.status)}</span>
              <span className="text-sm text-muted" dir="ltr">{formatDate(row.createdAt)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
